import { addParamsToUrl } from 'utils/query';

import { companyApi } from '../company';

import {
  TimeOffRow,
  TimeOffList,
  TimeOffBlock,
  TimeOffSettings,
  TimeOffListRequest,
  CreateTimeOffRequest,
  TimeOffDecisionRequest,
  TimeOffDecisionResponse,
  CreateTimeOffBlockRequest,
} from './types';

// TIME OFF (owner 24 Oct 2026; plan TIME-OFF-BUILD-PLAN-v2.md §4):
// /api/v1/time-off. Injected into the company API so the store's reducer
// list stays as it is; its own "TimeOff" tag refreshes the open lists after
// a change.
const TAG = 'TimeOff';

// The bell asks every minute, and only while the switch is on.
export const TIME_OFF_POLL_MS = 60_000;

export const timeOffApi = companyApi
  .enhanceEndpoints({ addTagTypes: [TAG] })
  .injectEndpoints({
    endpoints: (build) => ({
      timeOffSettings: build.query<TimeOffSettings, void>({
        providesTags: [TAG],
        query: () => ({ method: 'get', url: 'time-off/settings' }),
      }),
      timeOffBlocks: build.query<{ rows: TimeOffBlock[] }, void>({
        providesTags: [TAG],
        query: () => ({ method: 'get', url: 'time-off/blocked-dates' }),
      }),
      deleteTimeOff: build.mutation<{ deleted: boolean }, number>({
        invalidatesTags: [TAG],
        query: (id) => ({ method: 'delete', url: `time-off/${id}` }),
      }),
      cancelTimeOff: build.mutation<{ request: TimeOffRow }, number>({
        invalidatesTags: [TAG],
        query: (id) => ({ method: 'post', url: `time-off/${id}/cancel` }),
      }),
      requestTimeOff: build.mutation<
        { request: TimeOffRow },
        CreateTimeOffRequest
      >({
        invalidatesTags: [TAG],
        query: (data) => ({ data, method: 'post', url: 'time-off' }),
      }),
      removeTimeOffBlock: build.mutation<{ deleted: boolean }, number>({
        invalidatesTags: [TAG],
        query: (id) => ({
          method: 'delete',
          url: `time-off/blocked-dates/${id}`,
        }),
      }),
      // The switch (Company Settings > Business information): Company Owner.
      setTimeOffEnabled: build.mutation<TimeOffSettings, boolean>({
        invalidatesTags: [TAG],
        query: (enabled) => ({
          method: 'put',
          data: { enabled },
          url: 'time-off/settings',
        }),
      }),
      timeOffList: build.query<TimeOffList, TimeOffListRequest>({
        providesTags: [TAG],
        query: ({ scope, filter, employeeId }) => ({
          method: 'get',
          url: addParamsToUrl('time-off', { scope, filter, employeeId }),
        }),
      }),
      blockTimeOffDates: build.mutation<
        { row: TimeOffBlock },
        CreateTimeOffBlockRequest
      >({
        invalidatesTags: [TAG],
        query: (data) => ({
          data,
          method: 'post',
          url: 'time-off/blocked-dates',
        }),
      }),
      decideTimeOff: build.mutation<
        TimeOffDecisionResponse,
        TimeOffDecisionRequest
      >({
        invalidatesTags: [TAG],
        query: ({ id, ...data }) => ({
          data,
          method: 'post',
          url: `time-off/${id}/decision`,
        }),
      }),
    }),
  });

export const {
  useTimeOffListQuery,
  useTimeOffBlocksQuery,
  useTimeOffSettingsQuery,
  useCancelTimeOffMutation,
  useDecideTimeOffMutation,
  useDeleteTimeOffMutation,
  useRequestTimeOffMutation,
  useSetTimeOffEnabledMutation,
  useBlockTimeOffDatesMutation,
  useRemoveTimeOffBlockMutation,
} = timeOffApi;

export * from './types';
